import type * as Y from 'yjs';
import type { FormattingState } from '@/features/canvas/engine/hooks/use-formatting-state';
import { FillBorderGroup } from '../../primitives/formatting';
import { IconPickerButton } from '../../primitives/formatting/icon-picker-button';
import { canvasRoots } from '@/features/canvas/engine/wiring/canvas-roots';
import { useCanvasBinding } from '@/features/canvas/engine/wiring/contexts/canvas-binding-context';

export interface FrameFormattingToolbarProps {
  state: FormattingState;
  /** Live frame data — needed for the icon picker + Yjs writers. The
   *  fill / stroke colors are read from useFormattingState (state.fillColor,
   *  state.strokeColor) — same channel Sticky and Shape use. */
  doc: Y.Doc;
  nodeId: string;
  icon: string;
}

/**
 * Frame-flavoured formatting toolbar. The frame's title is edited
 * inline on the title bar (double-click → text input), so this toolbar
 * doesn't expose font / text formatting controls — only the
 * frame-specific surface:
 *
 *   - FillBorderGroup — merged Fill/Border tabbed popover, drives
 *     data.fill + data.stroke + data.strokeStyle through useFormattingState.
 *   - IconPickerButton — picks the closed-state Material Symbols icon.
 *
 * No wrapper div — fragment intended to be inlined inside
 * FrameFloatingToolbar so the parent owns the single role="toolbar" root.
 */
export function FrameFormattingToolbar({
  state,
  doc,
  nodeId,
  icon: iconName,
}: FrameFormattingToolbarProps) {
  const binding = useCanvasBinding();
  const writeData = (key: string, value: unknown) => {
    const nodes = canvasRoots(doc, binding).nodes;
    const node = nodes.get(nodeId);
    if (!node) return;
    const data = node.get('data');
    if (!data || typeof data !== 'object' || !('set' in data)) return;
    doc.transact(() => (data as Y.Map<unknown>).set(key, value), doc.clientID);
  };

  return (
    <>
      <FillBorderGroup state={state} />
      <IconPickerButton
        value={iconName}
        onChange={(next) => writeData('icon', next)}
      />
    </>
  );
}
